import { useReducer, useRef, useState, type ReactNode } from 'react';
import { useStableCallback } from '../../shared/use_stable_callback.js';
import type { FlightPinControl } from '../results/offer/offer_list.js';
import { usePhoneViewport } from '../results/offer/use_phone_viewport.js';
import { ExplorerEnvProvider, useExplorerEnv, type FlightExplorerEnv } from './explorer_env.js';
import type { FlightExplorerSearchSlots } from './explorer_slots.js';
import type { FlightExplorerTakeover } from './explorer_top_bar.js';
import type { FlightExplorerOffer } from './search_source.js';
import {
  activeOffers,
  initialExplorerState,
  legSearchParams,
  reduceExplorer,
  visibleOffers,
  type ExplorerConfirm,
  type ExplorerPick,
  type ExplorerState,
} from './explorer_state.js';
import { faresStageNode, makeSelectFares, useFaresStageHost } from './explorer_fares_stage.js';

import { type FlightExplorerRequest, type FlightSearchFn } from './search_source.js';
import { useServerFilter } from './use_server_filter.js';

import { useResumeSeq } from './use_resume_seq.js';
import { cabinSeam, loadMoreSeam } from './explorer_seams.js';
import { useInitialSearch } from './use_initial_search.js';
import { useFilterIntent } from './use_filter_intent.js';
import { stepsFor } from './explorer_steps.js';

import { ExplorerResultsPage, type ExplorerShare } from './explorer_results.js';

import { type SummaryPillTarget } from '../../shared/summary_pill/summary_pill.js';
import '../results/offer/flight_offers.css';
import './explorer.css';
import {
  useExplorerIo,
  useExplorerFares,
  takePickFor,
  makePickFlight,
  useExplorerPins,
  replaceHintOf,
  researchFor,
} from './explorer_io.js';
import {
  ExplorerShell,
  ExplorerFooter,
  ExplorerBody,
  ColumnBand,
  pillNodeFor,
} from './explorer_body.js';

/** A superfície pinada pelos grupos J/K — NÃO renomear props sem tocar lá. */
export interface FlightExplorerPageProps {
  /** O pedido normalizado por pernas (root export; J monta do BookingRequest,
   *  I monta do echo `search` do card). */
  request: FlightExplorerRequest;
  /** Lista já em mãos do host, POR PERNA (índice = perna): one_way passa
   *  `[allOffers]` do card, o roteiro passa o trio de cada trecho. Cada perna
   *  semeada abre sem esqueleto e re-busca por cima (`seeded`). RT nunca semeia —
   *  os refs dos combos são de VOLTA, inúteis pro return-search. */
  seedOffers?: FlightExplorerOffer[][] | undefined;
  /** Âncora de reuso SEM semente (#5793): a ref de qualquer oferta que o host já
   *  tem da MESMA busca — o card RT, que não semeia. Só a busca de mount da perna
   *  `idle` do one-way/RT a manda (ver `FlightSearchParams.anchorOfferId`); a
   *  semente, quando existe, vence. */
  reuseAnchorOfferId?: string;
  /** Perna em que o stepper ABRE — o trecho de onde a pessoa clicou. */
  initialLeg?: number;
  /** Trechos que o host já fechou: o stepper abre com eles resolvidos e o confirm
   *  agregado espera só o que falta. */
  initialChosen?: Partial<Record<number, ExplorerPick>>;
  /** Poda pré-mount (ver `initialExplorerState.initialPicksDiscarded`). */
  initialPicksDiscarded?: boolean;
  /** Ações do HOST no canto esquerdo do chrome — na prática o ✕ que SAI da
   *  tela: quem monta o explorer é quem sabe pra onde ele volta. Barra única
   *  (Task 5): a `.fex-top` só existe quando há `chrome` OU `pill`
   *  (`bandAsPill`) — sem os dois o host não desenha barra nenhuma
   *  (`fex--bare`), e o título mora na coluna. A bancada passa o próprio ✕
   *  aqui; a casca do chat passa o dela + `bandAsPill`. */
  chrome?: ReactNode;
  /** Ações do HOST no canto DIREITO do chrome (#3301) — no takeover do chat, a
   *  mala da viagem: o takeover cobre o top bar do shell (onde ela mora) e a
   *  pessoa perdia o itinerário justamente quando precisa dele. Também segura
   *  a barra (`hasBar`), como `chrome` e a pill. A bancada não passa: lá o top
   *  bar do shell segue visível. */
  trailing?: ReactNode;
  /** Congela SEM desmontar: faixa/painel inertes, escada view-only, CTA sai. */
  readOnly?: boolean;
  /** O host está PROCESSANDO o `onConfirm` (a bancada cotando antes do
   *  checkout): com `readOnly`, o CTA da tela de tarifas fica em cena girando —
   *  no celular a tela cheia cobre as faixas do host. Ver `ExplorerFaresStage`. */
  confirming?: boolean;
  /** Degelo do voltar-do-checkout (#2114): cada INCREMENTO derruba o
   *  `confirmed` e re-arma o single-fire — picks, listas e filtro ficam como
   *  estavam, e o Confirmar volta a poder emitir. O valor no mount é a linha de
   *  base (não descongela nada); só a MUDANÇA dispara. A casca do chat não usa:
   *  lá o host fecha o drawer no confirm. */
  resumeSeq?: number;
  /**
   * Este explorer foi aberto PELO CARD MULTI: confirmar aqui substitui a viagem
   * daquele card. Prop SEMÂNTICA e explícita (spec, rev 5 + rodada 7): não pode
   * derivar de `legIndex` (o roteiro completo pós-#1510 o omite) nem da
   * superfície (a bancada é a MESMA tela). Liga o aviso da ShellBar quando o
   * pedido corrente DIVERGE do de abertura; a frase completa ("descarta as
   * escolhas dos outros trechos") exige `picksDiscarded`.
   */
  replacesMultiCard?: boolean;
  /** As peças de BUSCA do host (#2113, #2129/#2130): a faixa na coluna, ou a
   *  pill na barra com o planner que ela expande sob a barra (a lista fica
   *  atrás do scrim). Sem nenhuma (a bancada, cuja busca vive na pill do shell)
   *  a coluna não tem faixa, e sem faixa não existe rascunho — o Confirmar
   *  nunca trava por `draftPending`. */
  slots?: FlightExplorerSearchSlots | undefined;
  /** A trilha e a fila de chips da barra quando o explorer cobre a tela do
   *  host (a casca do chat, #4003). */
  takeover?: FlightExplorerTakeover | undefined;
  /** A busca do host (o BFF), sem a qual não há explorer. */
  search: FlightSearchFn;
  /** Os textos, o cadastro e as portas do host (#4997). */
  env: FlightExplorerEnv;
  /** #2339 — "Nova busca" da empty page (zero real do fornecedor): reabre o
   *  planner com os campos preenchidos. A bancada passa o seu `onBackToSearch`;
   *  a casca do chat não plumba (lá só o "Tentar novamente" entra na empty
   *  page). Não confundir com o vazio-por-filtro, que segue com a sua copy. */
  onNewSearch?: () => void;
  /** Fixar voos "para comprar depois" (#2294) — capacidade injetada pelo host.
   *  Presente ⇒ cada linha ganha o toggle "Fixar" e a coluna ganha o filtro
   *  "Fixados (N)" (mostra só os fixados VIVOS da busca corrente). Ausente (a
   *  casca do chat) ⇒ nada de pino, exatamente como hoje. Só a bancada arma —
   *  a fixação é um atalho da TELA DE PESQUISA. */
  pins?: FlightPinControl<FlightExplorerOffer>;
  /** O compartilhar da busca, ao lado do título da página — o host monta o link e,
   *  com `onDownloadPdf`, a cotação em PDF da perna em cena (#5156). */
  share?: ExplorerShare;
  /** A cabine mudou na coluna de cortes (re-busca): o host que espelha o pedido
   *  na URL/link compartilhado grava a cabine junto dos outros critérios (#3239
   *  C5). `undefined` = economy/sem cabine. A casca do chat não passa. */
  onCabinChange?: (cabin: FlightExplorerRequest['cabin']) => void;
  /** A tela de tarifas entrou ou saiu de cena (#3989): recebe a volta à lista
   *  enquanto ela está montada e `null` fora dela. A bancada do web-chat
   *  desenha com isso a migalha `Tarifas` na trilha do shell e aponta o ← da
   *  barra para a lista (antes ele herdava a barra da lista e pulava ao
   *  formulário). A casca do chat não passa. */
  onFaresStage?: (back: (() => void) | null) => void;
  /** O verbo do CTA da tela de tarifas no pick FINAL. Ausente ⇒ "Ir para
   *  checkout" (a bancada, onde o confirm abre o checkout). A casca do chat
   *  passa "Confirmar": lá o confirm devolve a escolha à conversa, e o checkout
   *  é assunto de outro turno. Único ponto em que os dois hosts divergem depois
   *  da escolha da tarifa. */
  faresCtaLabel?: string | undefined;
  onConfirm: (confirm: ExplorerConfirm) => void;
}

/**
 * O explorer server-backed de voos — UMA superfície só, montada em tela cheia
 * pelos dois hosts: a casca do chat (`FlightExplorerDialog`, takeover por cima da
 * conversa) e a bancada (`BancadaView`, a seção inteira). Cada um pendura o
 * próprio ✕ no slot `chrome`; o resto — chrome, cortes, lista, rodapé — é o mesmo
 * desenho. Faixa de re-busca (o `PlannerForm`, sobre o BFF via `search` prop,
 * fallback `GenUiConfig.flightSearch`), eixos de filtro que cortam no SERVIDOR
 * quando o seam traz `refine` (e degradam pro corte na página em mão quando não),
 * RT em 2 fases e multi de N pernas (fan-out no mount) nos DOIS hosts, escada
 * compartilhada (`OfferListBody`), confirm ÚNICO por `onConfirm`.
 */
export function FlightExplorerPage({ env, ...props }: Readonly<FlightExplorerPageProps>) {
  return (
    <ExplorerEnvProvider env={env}>
      <ExplorerPage {...props} />
    </ExplorerEnvProvider>
  );
}

/** Rascunho da faixa: campos editados sem Buscar. Trava o Confirmar — a tela
 *  nunca mostra um roteiro que o confirm não veria (o confirm lê state.request).
 *  A faixa remonta na troca de perna e na troca de rota do pedido (a chave do
 *  `ColumnBand`): o form novo nasce limpo, o estado acompanha — tela e trava
 *  nunca divergem (reset during render). */
function useBandDirty(state: ExplorerState, bandLeg: number) {
  const [bandDirty, setBandDirty] = useState(false);
  const { origin, destination } = legSearchParams(state.request, bandLeg);
  const band = JSON.stringify([bandLeg, origin, destination]);
  const [dirtyBand, setDirtyBand] = useState(band);
  if (dirtyBand !== band) {
    setDirtyBand(band);
    setBandDirty(false);
  }
  return [bandDirty, setBandDirty] as const;
}

function ExplorerPage({
  request,
  seedOffers,
  reuseAnchorOfferId,
  initialLeg,
  initialChosen,
  initialPicksDiscarded,
  chrome,
  trailing,
  readOnly,
  confirming,
  resumeSeq,
  replacesMultiCard,
  slots,
  takeover,
  search: fs,
  onNewSearch,
  pins,
  share,
  onCabinChange,
  onFaresStage,
  faresCtaLabel,
  onConfirm,
}: Readonly<Omit<FlightExplorerPageProps, 'env'>>) {
  const { copy, locale, track } = useExplorerEnv();
  const [state, send] = useReducer(
    reduceExplorer,
    { request, seedOffers, initialLeg, initialChosen, initialPicksDiscarded },
    initialExplorerState,
  );
  const io = useExplorerIo(fs, state, send, onCabinChange);
  const sentRef = useRef(false);
  // No multi a faixa mostra o roteiro INTEIRO, mas a COLUNA re-busca a perna ATIVA.
  const bandLeg = state.request.mode === 'multi_leg' ? state.activeLeg : 0;
  const [bandDirty, setBandDirty] = useBandDirty(state, bandLeg);
  // Planner da pill expandido (`bandAsPill`): UI transiente — abrir/recolher
  // nunca toca em `state.request`; recolher descarta o rascunho inteiro.
  const [pillTarget, setPillTarget] = useState<SummaryPillTarget | null>(null);

  useInitialSearch(readOnly, state, io, reuseAnchorOfferId);

  const rows = visibleOffers(state);
  const offers = activeOffers(state);
  // Os cortes do painel rodando no SERVIDOR: devolve as facetas do conjunto e manda
  // a página refiltrada pro reducer. Ele mesmo decide qual perna ancora e que fase
  // não refina — ver `useServerFilter`.
  const serverFilter = useServerFilter(fs, state, send, readOnly);
  const { anchor, facets, refining, searchCeiling } = serverFilter;
  const fares = useExplorerFares(state, rows);
  useResumeSeq(resumeSeq, sentRef, send, state, fares);
  const frozen = Boolean(readOnly) || state.confirmed;
  useFaresStageHost(state, send, frozen, onFaresStage);
  // O composer "Filtre do seu jeito" da coluna de cortes: o seam interpreta o texto e o
  // reducer aplica os patches — quem refina segue sendo o `useServerFilter`, pelo mesmo
  // `refine`. `undefined` (sem seam ou congelado) ⇒ a coluna não desenha composer nenhum.
  const intent = { fs, anchor, state, send, offers, facets, frozen };
  const textFilter = useFilterIntent({ ...intent, locale, track });
  // Pino "comprar depois" (#2294): estado + derivações fora do corpo (o
  // componente vive no teto de complexidade/linhas).
  const { pin: pinControl, displayRows, pinnedFilter } = useExplorerPins(pins, frozen, rows);
  const pillNode = pillNodeFor({
    slot: slots?.pill,
    state,
    pillTarget,
    frozen,
    onExpand: setPillTarget,
    onSearch: io.retry,
  });
  // Scroll infinito da lista — ver `loadMoreSeam`.
  const replaceHint = replaceHintOf(request, state, replacesMultiCard);

  // Trava de rascunho: campo vivo na faixa OU planner da pill aberto — nos dois
  // casos a tela mostra um pedido que o confirm (lê state.request) não veria.
  // Vem ANTES do `takePick`: o pick final lê esta trava no mesmo gesto que
  // decide se confirma.
  const draftLock = bandDirty || pillTarget !== null;
  // A tela de tarifa no celular abre na casca de tela cheia da conversa (ver
  // `ExplorerFaresStage`); a pergunta é sobre a VIEWPORT, não sobre a coluna.
  const phone = usePhoneViewport();
  // Stable, and built on the pick itself: the handler reads `sentRef`, never the render.
  const takePick = useStableCallback((pick: Required<ExplorerPick>) => {
    takePickFor({ state, fares, send, sentRef, readOnly, draftLock, onConfirm })(pick);
  });

  // A fase de tarifa (#2262, todo modo desde a #3875): no desktop a tela OCUPA
  // a coluna — retorno ANTECIPADO, e não um ternário no corpo: nada do que vem
  // abaixo (painel, cabine, faixa, rodapé de contagem) tem uso lá — e o
  // `FlightExplorer` vive no teto de complexidade. No celular ela abre POR CIMA
  // da lista, na casca de tela cheia: a lista fica de pé atrás, e é exatamente
  // ela que reaparece quando o voltar da casca devolve a perna.
  const shell = {
    state,
    chrome,
    trailing,
    takeover,
    planner: slots?.planner,
    pill: pillNode,
    pillTarget,
    bandLeg,
    io,
    onClosePill: () => {
      setPillTarget(null);
    },
  };
  const stageDeps = { state, fares, frozen, draftLock, confirming, phone, send, takePick };
  const ctaLabel = faresCtaLabel ?? copy.fares.checkout;
  const faresStage = faresStageNode({ ...stageDeps, ctaLabel, onRetry: io.retry });
  if (faresStage && !phone) {
    return (
      // O `chrome` (o ✕ do host) FICA: a barra do topo é a mesma em toda etapa,
      // como a da bancada — sair pelo ✕ aqui é o mesmo gesto que lá.
      <ExplorerShell {...shell}>
        <div className="fex-results">{faresStage}</div>
      </ExplorerShell>
    );
  }

  // A cabine vive na coluna de cortes do `ResultsLayout` (dentro do
  // `FlightFilterPanel`): com a lista vazia ela é o único caminho de volta —
  // uma busca por Primeira classe zera a lista com facilidade.
  const cabinControl = cabinSeam(
    state,
    frozen,
    bandLeg,
    researchFor(io, state, bandLeg),
    onCabinChange,
  );
  const band = (
    <ColumnBand
      state={state}
      bandLeg={bandLeg}
      slot={slots?.band}
      frozen={frozen}
      io={io}
      onDirtyChange={setBandDirty}
    />
  );

  return (
    <ExplorerShell {...shell} footer={<ExplorerFooter state={state} />}>
      <ExplorerResultsPage
        state={state}
        steps={stepsFor(state)}
        rows={displayRows}
        offers={offers}
        frozen={frozen}
        refining={refining}
        band={band}
        hints={{ replaceHint, draftPending: draftLock, confirmStuck: state.confirmStuck }}
        send={send}
        page={loadMoreSeam(state, readOnly, serverFilter, io)}
        pinnedFilter={pinnedFilter}
        facets={facets}
        cabin={cabinControl}
        textFilter={textFilter}
        share={share}
      >
        <ExplorerBody
          state={state}
          rows={displayRows}
          frozen={frozen}
          fares={fares}
          searchCeiling={searchCeiling}
          refining={refining}
          {...(onNewSearch ? { onNewSearch } : {})}
          onRetry={io.retry}
          onSearch={io.runSearch}
          onPickFlight={makePickFlight(state, io, send)}
          onSelectFares={makeSelectFares(takePick)}
          pin={pinControl}
        />
      </ExplorerResultsPage>
      {faresStage}
    </ExplorerShell>
  );
}
